/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback} from 'react';
import {useSelector} from 'react-redux';
import Device from '@ion/core/src/services/Device';
import {FindNearbyType} from '@ion/core/src/types/entities';
import {
  findNearbyIsActiveSelector,
  findNearbySelector,
  selectedAssetSelector
} from '@/store/selectors/tracking-selectors';

/**
 * The useFindNearbyFilter hook returns the results of Find Nearby,
 * the results does not include the selected asset.
 *
 * ```typescript
 *
 *   import {useFindNearbyFilter} from './hooks/tracking/useFindNearbyFilter';
 *
 *   const removeSelectedAssetFindNearby = useFindNearbyFilter();
 *
 * ```
 */
export const useFindNearbyFilter = () => {
  const findNearbyIsActive = useSelector(findNearbyIsActiveSelector);
  const findLabel          = useSelector(findNearbySelector);
  const selectedAsset      = useSelector(selectedAssetSelector);

  const removeSelectedAssetFindNearby = useCallback((findNearbyResults: Device[]): Device[] => {
    const isLocation = findLabel?.type === FindNearbyType.location;
    const isGeofence = findLabel?.type === FindNearbyType.geofence;
    if (findNearbyIsActive && selectedAsset && findNearbyResults?.length && !isLocation && !isGeofence) {
      return findNearbyResults.filter((item: Device) => item.id() !== selectedAsset.id());
    }
    return findNearbyResults;
  }, [findNearbyIsActive, findLabel, selectedAsset]);

  return removeSelectedAssetFindNearby;
};
